import { HttpClient } from '@angular/common/http';
import { UserNotifications, Notification } from '@/app/notifications/types/user-notifications.type';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { HttpService } from '@/app/shared/services/http.service';
import { environment } from '@/environments/environment';
import { AuthHelper } from '@/app/shared/helpers/auth.helper';
import { BehaviorSubject, interval, Observable, Subject, of } from 'rxjs';
import { catchError, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const NOTIFICATIONS_POLL_INTERVAL_MS = 5000;

@Injectable({
    providedIn: 'root',
})
export class NotificationsService extends HttpService<UserNotifications> {
    private endpoint = environment.api.notifications;
    private destroyRef = inject(DestroyRef);
    private httpClient = inject(HttpClient);
    private authHelper = inject(AuthHelper);
    private unreadCountSubject = new BehaviorSubject<number>(0);
    /** null = not loaded yet; shared with notifications page for live polling updates */
    private notificationsListSubject = new BehaviorSubject<Notification[] | null>(null);
    private stopPolling$ = new Subject<void>();
    private isPollingActive = false;

    public unreadCount$ = this.unreadCountSubject.asObservable();
    public notificationsList$ = this.notificationsListSubject.asObservable();

    constructor(http: HttpClient) {
        super(http);
    }

    getNotifications(): Observable<Notification[]> {
        return this.getOne(this.endpoint).pipe(
            map(response => response.notifications)
        );
    }

    markAsRead(notificationId: number): Observable<Notification> {
        return this.httpClient.patch<Notification>(`${this.baseUrl}${this.endpoint}/${notificationId}/read`, { hasRead: true });
    }

    markAllAsRead(): Observable<any> {
        return this.httpClient.patch(`${this.baseUrl}${this.endpoint}/read-all`, {});
    }

    deleteNotification(notificationId: number): Observable<any> {
        return this.httpClient.delete<any>(`${this.baseUrl}${this.endpoint}/${notificationId}`);
    }

    private updateNotificationsState(notifications: Notification[]): void {
        this.notificationsListSubject.next(notifications);
        this.unreadCountSubject.next(notifications.filter(n => !n.hasRead).length);
    }
    /** Immediate fetch (e.g. after mark read / delete); keeps list + sidebar count in sync */
    refreshNotificationsNow(): void {
        if (!this.authHelper.isUserLogged()) {
            this.updateNotificationsState([]);
            return;
        }

        this.getNotifications()
            .pipe(
                take(1),
                catchError(error => {
                    console.error('Error fetching notifications:', error);
                    return of([] as Notification[]);
                })
            )
            .subscribe(list => this.updateNotificationsState(list));
    }

    startPolling(): void {
        if (this.isPollingActive) {
            return;
        }

        this.isPollingActive = true;
        interval(NOTIFICATIONS_POLL_INTERVAL_MS)
            .pipe(
                startWith(0),
                switchMap(() =>
                    !this.authHelper.isUserLogged()
                        ? of([] as Notification[])
                        :
                        this.getNotifications().pipe(
                            catchError(error => {
                                console.error('Error fetching notifications:', error);
                                return of([] as Notification[]);
                            })
                        )
                ),
                takeUntil(this.stopPolling$),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(notifications => {
                this.updateNotificationsState(notifications);
            });
    }

    stopPolling(): void {
        this.stopPolling$.next();
        this.isPollingActive = false;
    }
}
